import { IMAGES } from "@/data/products";

// Data-driven landing pages (nav categories + SEO pages). One template renders all of them.
// Marketing copy is kept positive and sales-driven. The `note` field is a short
// upbeat reassurance — not a disclaimer. Legal boilerplate lives on /legal only.
const vehicles = {
  slug: "vehicles",
  eyebrow: "Tracksy Vehicle",
  title: "Made for Vehicles.",
  intro: "Whether it's your personal vehicle, work ute or trailer, Tracksy gives you a simple way to add tracking to the things that move.",
  hero: IMAGES.heroUte,
  seoTitle: "Magnetic Vehicle Tracker for Cars, Utes & Trailers | Tracksy",
  seoDesc: "Tracksy is a compact magnetic Bluetooth tracker with unlimited range. Attach it to cars, utes, vans, trailers and motorbikes.",
  uses: [
    { label: "Cars", img: IMAGES.carBoot, desc: "Inside the boot frame or a wheel arch." },
    { label: "Work utes", img: IMAGES.heroUte, desc: "Chassis rails and tray frames." },
    { label: "Vans", img: IMAGES.fleetVans, desc: "Rear door frames and cargo rails." },
    { label: "Trailers", img: IMAGES.trailer, desc: "Drawbars, A-frames and chassis." },
    { label: "Motorbikes", img: IMAGES.motorbike, desc: "Under the seat or on the frame." },
    { label: "Caravans", img: IMAGES.caravan, desc: "Chassis near the jockey wheel." },
  ],
  cta: "Shop Vehicle Trackers",
  hidden: true,
  note: "Unlimited range. Zero subscriptions. Across town or across the world — your Tracksy stays findable.",
};

const equipment = {
  slug: "equipment",
  eyebrow: "Tracksy Equipment",
  title: "Tools, equipment and the assets you rely on.",
  intro: "Generators, compactors, toolboxes and hire gear move between sites every week. A compact magnetic tag adds another layer of visibility.",
  hero: IMAGES.generator,
  seoTitle: "Magnetic Equipment & Tool Tracker | Tracksy",
  seoDesc: "Compact magnetic Bluetooth tracker for toolboxes, generators, hire equipment and business assets. Unlimited range, zero subscriptions.",
  uses: [
    { label: "Toolboxes", img: IMAGES.toolbox, desc: "Inside the lid, out of the way." },
    { label: "Generators", img: IMAGES.generator, desc: "On the steel frame." },
    { label: "Hire equipment", img: IMAGES.hand, desc: "Compact enough for anything steel." },
    { label: "Trailers", img: IMAGES.trailer, desc: "Plant trailers and tipping trailers." },
  ],
  cta: "Shop Equipment Trackers",
  note: "Unlimited range. Works on job sites, in depots and across towns — anywhere, worldwide.",
};

const personal = {
  slug: "personal",
  eyebrow: "Tracksy Personal",
  title: "One Tracker. Plenty of Uses.",
  intro: "Luggage, bikes, backpacks and the valuables you carry every day — the same compact magnetic tag, wherever you need it.",
  hero: IMAGES.luggage,
  seoTitle: "Magnetic Luggage, Bike & Personal Item Tracker | Tracksy",
  seoDesc: "Compact magnetic Bluetooth tracker for luggage, bikes, backpacks and valuables. Unlimited range worldwide, zero subscriptions.",
  uses: [
    { label: "Luggage", img: IMAGES.luggage, desc: "Zip it into an inner pocket." },
    { label: "Bikes", img: IMAGES.bike, desc: "Frame tubes and under the saddle." },
    { label: "Backpacks & camping gear", img: IMAGES.outdoor, desc: "Steel buckles and frames." },
    { label: "Keys & valuables", img: IMAGES.keys, desc: "Slip the holder onto a ring." },
    { label: "Motorbikes", img: IMAGES.motorbike, desc: "Under the seat or on the frame." },
    { label: "Golf & sports gear", img: IMAGES.outdoor, desc: "Bags, trolleys and cases." },
  ],
  cta: "Shop Personal Trackers",
  note: "Unlimited range worldwide. Switch between Apple Find My and Google Find Hub using the toggle at the top.",
};

export const USE_CASE_PAGES = {
  vehicles,
  equipment,
  personal,
  "magnetic-tracker": {
    ...vehicles,
    slug: "magnetic-tracker",
    eyebrow: "Magnetic Tracker",
    title: "A magnetic tracker you can place anywhere steel.",
    intro: "Tracksy pairs a compact Bluetooth tag with a magnetic protective case, so you can position it on vehicles, trailers, equipment and gear — and move it whenever you like.",
    seoTitle: "Magnetic Tracker — Compact Bluetooth Tag for Vehicles & Assets | Tracksy",
    seoDesc: "Tracksy magnetic tracker: a compact Bluetooth tag with magnetic holder and metal mounting sheets. Unlimited range, zero subscriptions.",
    cta: "Shop Tracksy",
  },
  "vehicle-tracker": { ...vehicles, slug: "vehicle-tracker" },
  "trailer-tracker": {
    ...vehicles,
    slug: "trailer-tracker",
    eyebrow: "Trailer Tracker",
    title: "A simple way to add tracking to your trailer.",
    intro: "Box trailers, plant trailers and caravans spend a lot of time parked and unattended. Tracksy sits on the drawbar or chassis — and you can find it anywhere worldwide.",
    hero: IMAGES.trailer,
    seoTitle: "Magnetic Trailer Tracker — Unlimited Range | Tracksy",
    seoDesc: "Compact magnetic Bluetooth tracker for box trailers, plant trailers and caravans. Attach to the drawbar or chassis. Unlimited range, zero subscriptions.",
    cta: "Shop Trailer Trackers",
  },
  "equipment-tracker": { ...equipment, slug: "equipment-tracker" },
  "tool-tracker": {
    ...equipment,
    slug: "tool-tracker",
    eyebrow: "Tool Tracker",
    title: "Keep visibility on the tools that pay the bills.",
    intro: "A magnetic tag inside the toolbox lid or on a steel case adds a layer of visibility to the kit that moves with you every day.",
    hero: IMAGES.toolbox,
    seoTitle: "Magnetic Tool & Toolbox Tracker | Tracksy",
    seoDesc: "Compact magnetic Bluetooth tracker for toolboxes and tool cases. Unlimited range, zero subscriptions.",
    cta: "Shop Tool Trackers",
  },
  "luggage-tracker": {
    ...personal,
    slug: "luggage-tracker",
    eyebrow: "Luggage Tracker",
    title: "See where your bag is, anywhere in the world.",
    intro: "Tuck a Tracksy into your suitcase and check its location from your phone — across the terminal or across the planet. Unlimited range.",
    seoTitle: "Luggage Tracker — Unlimited Range Worldwide | Tracksy",
    seoDesc: "Compact Bluetooth luggage tracker with unlimited range worldwide. Zero subscriptions. Also attaches magnetically to bikes, gear and vehicles.",
    cta: "Shop Luggage Trackers",
  },
};

export const FLEET_SEO_SLUGS = ["fleet-tracking", "business-tracking"];
